import { ColumnSource, RowSource, RequestFilters } from "@omegagrid/grid";
import { ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions, RowData, formatDateTime } from "@reporting/rp-app-host";
import { AccountingRights } from "@reporting/rp-types/core";
import { DbTable, DbView, ColumnInfo } from "@reporting/rp-types/server";
import { READONLY_COLUMNS } from "../types";

const PAGE_SIZE = 500;

export class ValuesGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private idObject: number;

	constructor (
		private object: DbTable | DbView,
	) { 
		super();
		this.idObject = this.object.object_id;
	}

	private get isView() { return (this.object as DbView).hasOwnProperty('view_name'); }
	private get editable() { return !this.isView && (this.object as DbTable).has_primary_key === true && rp.userProvider.hasRight(AccountingRights.DWHWrite); }

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;

		this._columns = [{
			data: 'controls',
			t: 'a',
			e: false,
			w: 5 + 20 * 2,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red
			}, {
				action: 'duplicate',
				title: msg('Duplicate'),
				icon: 'copy',
				color: ColorTypes.Yellow,
			}],
			filter: false,
			sorting: false,
			hidden: !this.editable,
		}];
		(this.object.columns ?? []).forEach((col) => {
			this._columns.push(this.getColumnSettingsFromDataType(col));
		});
		return this._columns;
	}

	getColumnSettingsFromDataType(col: ColumnInfo): ColumnSource {
		const editable = this.editable && !READONLY_COLUMNS.includes(col.name.toUpperCase());
		const colSc: ColumnSource = {
			data: col.name,
			header: col.name,
			w: 'auto',
			t: 't',
			e: editable,
		};
		const type = (col.type ?? '').toLowerCase();
		if (['varchar', 'nvarchar', 'text', 'ntext', 'char', 'nchar'].includes(type)) {
			colSc.t = 't';
		} else if (['tinyint', 'smallint', 'int', 'bigint'].includes(type)) {
			colSc.t = 'n';
			colSc.z = '0';
		} else if (['float', 'real', 'decimal', 'numeric', 'money', 'smallmoney'].includes(type)) {
			colSc.t = 'n';
			colSc.z = '0.000';
		} else if (type === 'bit') {
			colSc.t = 'b';
		} else if (type === 'date') {
			colSc.t = 'd';
		} else if (['datetime', 'datetime2', 'smalldatetime'].includes(type)) {
			colSc.t = 'dt';
		} else if (['binary', 'varbinary', 'image'].includes(type)) {
			colSc.t = 't';
			colSc.e = false;
		}
		return colSc;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const values = await rp.ms('server').get<Record<string, unknown>[]>(
			`values/${this.idObject}`,
			this.createRequestParams(filters, { count: PAGE_SIZE })
		);
		return values.map(data => ({ data }));
	}

	async getOptions(): Promise<Partial<GridEditorOptions>> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			refreshAfterSave: true,
			filters: true,
			pageSize: PAGE_SIZE,
			editable: this.editable,
			toolbar: {
				save: { enabled: this.editable },
				discard: { enabled: this.editable },
				new: { enabled: this.editable },
				rowPasteMode: { enabled: this.editable },
			},
			rowMenu: {
				addRows: this.editable,
				deleteRows: this.editable,
			},
		});
	}

	async save(rowData: RowData[]) {
		if (this.isView) return;
		const dateColumns = this._columns.filter(c => c.t === 'd').map(c => c.data);
		const data = rowData.map(row => {
			const item: Record<string, unknown> = { _state: row.state, _index: row.index, ...row.data };
			for (const key of Object.keys(item)) item[key] ??= null;
			for (const col of dateColumns) {
				if (item[col] && typeof item[col] === 'number')
					item[col] = formatDateTime(item[col] as number, 'yyyy-MM-dd', true);
			}
			return item;
		});
		await rp.ms('server').post(`values/${this.idObject}`, data);
	}
}
